import { Box, Button, Checkbox, Flex, Grid, Text, UnstyledButton } from '@mantine/core';
import { TbChevronDown, TbChevronUp, TbChevronRight, TbInfoCircle } from 'react-icons/tb';
import { FormInput } from '../../../../components/FormElements/FormInput';
import { FormSelect } from '../../../../components/FormElements/FormSelect';
import { ReactNode } from 'react';
import { Control, UseFormHandleSubmit } from 'react-hook-form';

interface CountrySectionProps {
  code: string;
  label: string;
  expandedCountryCodes: string[];
  onSelectCountry: (code: string) => void;
  children: ReactNode;
}

// Reusable CountrySection component
function CountrySection({
  code,
  label,
  expandedCountryCodes,
  onSelectCountry,
  children
}: CountrySectionProps) {
  const isExpanded = expandedCountryCodes.includes(code);

  return (
    <Flex id={`export-drawer-country-section-${code}`} gap="4px">
      {/* @ts-ignore */}
      <UnstyledButton
        id={`export-drawer-country-section-toggle-${code}`}
        onClick={() => onSelectCountry(code)}
        h="fit-content"
        d="flex"
        w="16px"
      >
        {isExpanded ? (
          <TbChevronUp size="16px" color="#C1C2C5" />
        ) : (
          <TbChevronDown size="16px" color="#C1C2C5" />
        )}
      </UnstyledButton>
      <Box id={`export-drawer-country-section-content-${code}`} flex="auto">
        <Text
          id={`export-drawer-country-section-label-${code}`}
          fz="14px"
          lh="20px"
          fw="600"
          c="black"
        >
          {label}
        </Text>
        {isExpanded && children}
      </Box>
    </Flex>
  );
}

interface SelectOption {
  value: string;
  label: string;
}

interface AdditionalInformationFormProps {
  control: Control<any>;
  handleSubmit: UseFormHandleSubmit<any>;
  onSubmit: (data: any) => void;
  jurisdictionsIds?: string[];
  expandedCountryCodes?: string[];
  onSelectCountry: (code: string) => void;
  reportingInstitutions?: SelectOption[];
  typeOfIds?: SelectOption[];
  exportWithoutEntries: boolean;
  setExportWithoutEntries: (val: boolean) => void;
  isDisabledExport?: boolean;
  isLoading?: boolean;
}

export function AdditionalInformationForm({
  control,
  handleSubmit,
  onSubmit,
  jurisdictionsIds = [],
  expandedCountryCodes = [],
  onSelectCountry,
  reportingInstitutions = [],
  typeOfIds = [],
  exportWithoutEntries,
  setExportWithoutEntries,
  isDisabledExport,
  isLoading
}: AdditionalInformationFormProps) {
  return (
    <form id="export-drawer-additional-information-form" onSubmit={handleSubmit(onSubmit)}>
      <Flex id="export-drawer-additional-information-content" direction="column" gap="16px">
        {jurisdictionsIds.includes('DK') && (
          <CountrySection
            code="DK"
            label="DENMARK"
            expandedCountryCodes={expandedCountryCodes}
            onSelectCountry={onSelectCountry}
          >
            <>
              <Grid w="100%" gutter="sm" grow mt="10px">
                <Grid.Col span={6} id="export-drawer-dk-grid-col-1">
                  <FormInput
                    id="export-drawer-dk-enterprise-registration-number"
                    name="idno"
                    control={control}
                    label="Enterprise Registration Number"
                    required
                  />
                </Grid.Col>
                <Grid.Col span={6} id="export-drawer-dk-grid-col-2">
                  <FormInput name="refperio" control={control} label="Reporting Period" required />
                </Grid.Col>
                <Grid.Col span={6} id="export-drawer-dk-grid-col-3">
                  <FormInput name="loebenr" control={control} label="Serial Number" required />
                </Grid.Col>
                <Grid.Col span={6} id="export-drawer-dk-grid-col-4" />
              </Grid>
              <Grid w="100%" gutter="sm" grow>
                <Grid.Col span={6} id="export-drawer-dk-grid-col-5">
                  <FormInput name="firmanavn" control={control} label="Company Name" />
                </Grid.Col>
                <Grid.Col span={6} id="export-drawer-dk-grid-col-6">
                  <FormInput name="gadenavn" control={control} label="Street Name" />
                </Grid.Col>
                <Grid.Col span={6} id="export-drawer-dk-grid-col-7">
                  <FormInput name="postnr" control={control} label="Postal Code" />
                </Grid.Col>
                <Grid.Col span={6} id="export-drawer-dk-grid-col-8">
                  <FormInput name="boks" control={control} label="Post Office Box Number" />
                </Grid.Col>
                <Grid.Col span={6} id="export-drawer-dk-grid-col-9">
                  <FormInput name="by" control={control} label="City" />
                </Grid.Col>
                <Grid.Col span={6} id="export-drawer-dk-grid-col-10">
                  <FormInput name="tlf" control={control} label="Phone Number" />
                </Grid.Col>
                <Grid.Col span={6} id="export-drawer-dk-grid-col-11">
                  <FormInput name="fuldtnavn" control={control} label="Contact Name" required />
                </Grid.Col>
                <Grid.Col span={6} id="export-drawer-dk-grid-col-12">
                  <FormInput
                    name="tlf_kontaktperson"
                    control={control}
                    label="Contact Phone"
                    required
                  />
                </Grid.Col>
                <Grid.Col span={6} id="export-drawer-dk-grid-col-13">
                  <FormInput name="email" control={control} label="Contact Email" required />
                </Grid.Col>
                <Grid.Col span={6} id="export-drawer-dk-grid-col-14" />
              </Grid>
            </>
          </CountrySection>
        )}

        {jurisdictionsIds.includes('FI') && (
          <CountrySection
            code="FI"
            label="FINLAND"
            expandedCountryCodes={expandedCountryCodes}
            onSelectCountry={onSelectCountry}
          >
            <>
              <Grid w="100%" gutter="sm" grow mt="10px">
                <Grid.Col span={6} id="export-drawer-fi-grid-col-1">
                  <FormSelect
                    id="export-drawer-fi-reporting-institution-aifm"
                    control={control}
                    name="typeofreportinginstitution_aifm"
                    label="Type of Reporting Institution (AIFM)"
                    options={reportingInstitutions as any}
                    disabled={exportWithoutEntries}
                  />
                </Grid.Col>
              </Grid>
              <Grid w="100%" gutter="sm" grow>
                <Grid.Col span={6} id="export-drawer-fi-grid-col-2">
                  <FormSelect
                    id="export-drawer-fi-reporting-institution-aif"
                    control={control}
                    name="typeofreportinginstitution_aif"
                    label="Type of Reporting Institution (AIF)"
                    options={reportingInstitutions as any}
                    disabled={exportWithoutEntries}
                  />
                </Grid.Col>
              </Grid>
              <Grid w="100%" gutter="sm" grow>
                <Grid.Col span={6} id="export-drawer-fi-grid-col-3">
                  <FormInput
                    id="export-drawer-fi-reporting-entity-id"
                    name="reportingentityId"
                    control={control}
                    label="Reporting Entity ID"
                    disabled={exportWithoutEntries}
                  />
                </Grid.Col>
                <Grid.Col span={6} id="export-drawer-fi-grid-col-4">
                  <FormSelect
                    id="export-drawer-fi-type-of-id"
                    control={control}
                    name="typeofId"
                    label="Type of ID"
                    options={typeOfIds as any}
                    disabled={exportWithoutEntries}
                  />
                </Grid.Col>
              </Grid>
            </>
          </CountrySection>
        )}

        {jurisdictionsIds.includes('LU') && (
          <CountrySection
            code="LU"
            label="LUXEMBOURG"
            expandedCountryCodes={expandedCountryCodes}
            onSelectCountry={onSelectCountry}
          >
            <Grid w="100%" gutter="sm" grow mt="10px">
              <Grid.Col span={6} id="export-drawer-lu-grid-col-1">
                <FormInput
                  id="export-drawer-lu-sender-id"
                  name="sender_id"
                  control={control}
                  label="Sender ID for Luxembourg"
                  disabled={exportWithoutEntries}
                />
              </Grid.Col>
            </Grid>
          </CountrySection>
        )}
      </Flex>
      <Flex
        id="export-drawer-additional-information-actions"
        align="center"
        gap="16px"
        justify="flex-end"
        mt="32px"
      >
        {(jurisdictionsIds.includes('LU') || jurisdictionsIds.includes('FI')) &&
          !jurisdictionsIds.includes('DK') && (
            <Flex id="export-drawer-export-without-entries" align="center" gap="6px">
              <TbInfoCircle color="#228BE6" fontSize="16px" />
              <Text fz="14px" c="#101113">
                Export without entries
              </Text>
              <Checkbox
                id="export-drawer-export-without-entries-checkbox"
                onChange={val => setExportWithoutEntries(val.target.checked)}
                size="xs"
                checked={exportWithoutEntries}
              />
            </Flex>
          )}
        <Button
          id="export-drawer-additional-information-export-button"
          disabled={isDisabledExport}
          rightSection={<TbChevronRight size="16" />}
          type="submit"
          size="xs"
          loading={isLoading}
        >
          Export
        </Button>
      </Flex>
    </form>
  );
}
